import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import { getApiUrl } from '../utils/api';
import { apiFetch } from '../utils/apiFetch';
import { useAuth } from '../auth/AuthContext';
import { canManageInputs } from '../utils/auth';

const API_URL = getApiUrl();

const emptyInputForm = {
  name: '',
  categoryId: '',
  purchaseUnit: 'Kg',
  quantityPerPackage: '',
  currentStockBase: '',
  minimumStockBase: '',
  purchaseCost: '',
  active: true,
};

const unitConversion = (purchaseUnit, quantityPerPackage) => {
  if (purchaseUnit === 'L') return { baseUnit: 'ml', conversionFactor: 1000 };
  if (purchaseUnit === 'Cx') return { baseUnit: 'un', conversionFactor: Number(quantityPerPackage || 0) };
  return { baseUnit: 'gr', conversionFactor: 1000 };
};

const formatCurrency = (value) => Number(value || 0).toLocaleString('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const toNumber = (value) => {
  const numeric = Number(String(value ?? '').replace(',', '.'));
  return Number.isFinite(numeric) ? numeric : 0;
};

const normalizeCatalogItemKeyPart = (value) => String(value || '')
  .trim()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const buildOptionTarget = (option = {}, group = {}) => {
  const rawKey = option.externalCode || option.optionId || option.id || option.code || option.sku || option.name;
  const normalizedKey = normalizeCatalogItemKeyPart(rawKey);
  if (!normalizedKey) return null;
  return {
    key: `option:${normalizedKey}`,
    type: 'catalog-item',
    itemType: 'option',
    itemKey: `option:${normalizedKey}`,
    label: option.name || option.optionName || 'Complemento',
    subtitle: group.name || group.groupName || 'Complemento',
  };
};

const fetchJson = async (url, options = {}) => {
  const response = await apiFetch(url, options);
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    throw new Error(data.error || 'Falha ao processar solicitação.');
  }
  return data;
};

const ProductTechnicalSheetDialog = ({ open, product, onClose }) => {
  const auth = useAuth();
  const canManage = canManageInputs(auth.user);
  const productId = product?.dbId;
  const productName = product?.itemName || product?.name || '';
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [inputs, setInputs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [inputSearch, setInputSearch] = useState('');
  const [selectedInputId, setSelectedInputId] = useState('');
  const [quantityBase, setQuantityBase] = useState('');
  const [quantityDrafts, setQuantityDrafts] = useState({});
  const [inputDialogOpen, setInputDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [targetKey, setTargetKey] = useState('product');
  const [inputForm, setInputForm] = useState(emptyInputForm);
  const [categoryName, setCategoryName] = useState('');
  const [quickCategoryName, setQuickCategoryName] = useState('');
  const [inputDialogError, setInputDialogError] = useState('');
  const [categoryDialogError, setCategoryDialogError] = useState('');
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [audit, setAudit] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);

  const technicalTargets = useMemo(() => {
    const options = (product?.itemOptionGroups || []).flatMap((group) => (
      (group.options || []).map((option) => buildOptionTarget(option, group)).filter(Boolean)
    ));
    const uniqueOptions = Array.from(new Map(options.map((item) => [item.key, item])).values());
    return [
      { key: 'product', type: 'product', label: 'Produto principal', subtitle: productName, productId },
      ...uniqueOptions,
    ];
  }, [product, productId, productName]);

  const selectedTarget = useMemo(
    () => technicalTargets.find((target) => target.key === targetKey) || technicalTargets[0],
    [targetKey, technicalTargets]
  );

  const loadData = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    setError('');
    try {
      const sheetRequest = selectedTarget?.type === 'catalog-item'
        ? fetchJson(`${API_URL}/api/inputs/catalog-items/${encodeURIComponent(selectedTarget.itemKey)}/technical-sheet?itemType=${encodeURIComponent(selectedTarget.itemType || 'option')}`)
        : productId ? fetchJson(`${API_URL}/api/inputs/products/${productId}/technical-sheet`) : Promise.resolve({ items: [], totalCost: 0 });
      const [inputData, categoryData, sheetData] = await Promise.all([
        fetchJson(`${API_URL}/api/inputs`),
        fetchJson(`${API_URL}/api/inputs/categories`),
        sheetRequest,
      ]);
      setInputs(inputData.inputs || []);
      setCategories(categoryData.categories || []);
      setItems(sheetData.items || []);
      setTotalCost(sheetData.totalCost || 0);
      setQuantityDrafts((sheetData.items || []).reduce((acc, item) => ({ ...acc, [item.id]: item.quantityBase }), {}));
    } catch (err) {
      setError(err.message || 'Falha ao carregar ficha técnica.');
    } finally {
      setLoading(false);
    }
  }, [open, productId, selectedTarget]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!open) {
      setSelectedInputId('');
      setQuantityBase('');
      setInputSearch('');
      setError('');
      setInputDialogOpen(false);
      setCategoryDialogOpen(false);
      setQuickCategoryName('');
      setInputDialogError('');
      setCategoryDialogError('');
      setAuditDialogOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (open) setTargetKey('product');
  }, [open, productId]);

  const selectedInput = useMemo(
    () => inputs.find((input) => String(input.id) === String(selectedInputId)),
    [inputs, selectedInputId]
  );

  const filteredInputs = useMemo(() => {
    const query = inputSearch.trim().toLowerCase();
    return inputs
      .filter((input) => input.active !== false)
      .filter((input) => !query || input.name.toLowerCase().includes(query) || input.categoryName.toLowerCase().includes(query));
  }, [inputSearch, inputs]);

  const conversion = unitConversion(inputForm.purchaseUnit, inputForm.quantityPerPackage);
  const baseUnitCost = conversion.conversionFactor > 0 ? toNumber(inputForm.purchaseCost) / conversion.conversionFactor : 0;

  const saveSheetItem = async () => {
    if (!canManage) return;
    if (selectedTarget?.type === 'product' && !productId) {
      setError('Salve o produto antes de cadastrar a ficha técnica.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const targetUrl = selectedTarget?.type === 'catalog-item'
        ? `${API_URL}/api/inputs/catalog-items/${encodeURIComponent(selectedTarget.itemKey)}/technical-sheet/items`
        : `${API_URL}/api/inputs/products/${productId}/technical-sheet/items`;
      const body = selectedTarget?.type === 'catalog-item'
        ? {
          inputId: selectedInputId,
          quantityBase: toNumber(quantityBase),
          itemType: selectedTarget.itemType,
          catalogItemName: selectedTarget.label,
        }
        : { inputId: selectedInputId, quantityBase: toNumber(quantityBase) };
      const data = await fetchJson(targetUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      setItems(data.items || []);
      setTotalCost(data.totalCost || 0);
      setQuantityDrafts((data.items || []).reduce((acc, item) => ({ ...acc, [item.id]: item.quantityBase }), {}));
      setSelectedInputId('');
      setQuantityBase('');
    } catch (err) {
      setError(err.message || 'Falha ao salvar ficha técnica.');
    } finally {
      setSaving(false);
    }
  };

  const updateSheetItem = async (itemId) => {
    if (!canManage) return;
    setSaving(true);
    setError('');
    try {
      const itemUrl = selectedTarget?.type === 'catalog-item'
        ? `${API_URL}/api/inputs/catalog-technical-sheet/items/${itemId}`
        : `${API_URL}/api/inputs/technical-sheet/items/${itemId}`;
      const data = await fetchJson(itemUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantityBase: toNumber(quantityDrafts[itemId]) }),
      });
      setItems(data.items || []);
      setTotalCost(data.totalCost || 0);
      setQuantityDrafts((data.items || []).reduce((acc, item) => ({ ...acc, [item.id]: item.quantityBase }), {}));
    } catch (err) {
      setError(err.message || 'Falha ao atualizar item.');
    } finally {
      setSaving(false);
    }
  };

  const removeSheetItem = async (itemId) => {
    if (!canManage) return;
    if (!window.confirm('Remover este insumo da ficha técnica?')) return;
    setSaving(true);
    setError('');
    try {
      const itemUrl = selectedTarget?.type === 'catalog-item'
        ? `${API_URL}/api/inputs/catalog-technical-sheet/items/${itemId}`
        : `${API_URL}/api/inputs/technical-sheet/items/${itemId}`;
      const data = await fetchJson(itemUrl, { method: 'DELETE' });
      setItems(data.items || []);
      setTotalCost(data.totalCost || 0);
      setQuantityDrafts((data.items || []).reduce((acc, item) => ({ ...acc, [item.id]: item.quantityBase }), {}));
    } catch (err) {
      setError(err.message || 'Falha ao remover item.');
    } finally {
      setSaving(false);
    }
  };

  const createCategory = async () => {
    if (!canManage) return;
    setSaving(true);
    setCategoryDialogError('');
    try {
      const data = await fetchJson(`${API_URL}/api/inputs/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: categoryName }),
      });
      setCategories((prev) => {
        const next = prev.filter((item) => item.id !== data.category.id);
        return [...next, data.category].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
      });
      setInputForm((prev) => ({ ...prev, categoryId: data.category.id }));
      setCategoryName('');
      setCategoryDialogOpen(false);
    } catch (err) {
      setCategoryDialogError(err.message || 'Falha ao criar categoria.');
    } finally {
      setSaving(false);
    }
  };

  const createQuickCategory = async () => {
    if (!canManage) return;
    const name = quickCategoryName.trim();
    if (!name) return;
    setSaving(true);
    setInputDialogError('');
    try {
      const data = await fetchJson(`${API_URL}/api/inputs/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      setCategories((prev) => {
        const next = prev.filter((item) => item.id !== data.category.id);
        return [...next, data.category].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
      });
      setInputForm((prev) => ({ ...prev, categoryId: data.category.id }));
      setQuickCategoryName('');
    } catch (err) {
      setInputDialogError(err.message || 'Falha ao criar categoria.');
    } finally {
      setSaving(false);
    }
  };

  const createInput = async () => {
    if (!canManage) return;
    setSaving(true);
    setInputDialogError('');
    try {
      const data = await fetchJson(`${API_URL}/api/inputs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...inputForm,
          categoryId: inputForm.categoryId || null,
          quantityPerPackage: inputForm.purchaseUnit === 'Cx' ? toNumber(inputForm.quantityPerPackage) : null,
          currentStockBase: toNumber(inputForm.currentStockBase),
          minimumStockBase: toNumber(inputForm.minimumStockBase),
          purchaseCost: toNumber(inputForm.purchaseCost),
        }),
      });
      setInputs((prev) => {
        const next = prev.filter((item) => item.id !== data.input.id);
        return [...next, data.input].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
      });
      setSelectedInputId(data.input.id);
      setInputForm(emptyInputForm);
      setInputDialogOpen(false);
    } catch (err) {
      setInputDialogError(err.message || 'Falha ao criar insumo.');
    } finally {
      setSaving(false);
    }
  };

  const openAuditDialog = async () => {
    setAuditDialogOpen(true);
    setLoadingAudit(true);
    try {
      const query = selectedTarget?.type === 'catalog-item'
        ? `catalogItemKey=${encodeURIComponent(selectedTarget.itemKey)}`
        : `productId=${encodeURIComponent(productId || '')}`;
      const data = await fetchJson(`${API_URL}/api/inputs/technical-sheet/audit?${query}`);
      setAudit(data.audit || []);
    } catch (err) {
      setError(err.message || 'Falha ao carregar auditoria.');
    } finally {
      setLoadingAudit(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Ficha técnica</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{productName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedTarget?.label || 'Ficha técnica'} • Custo total: {formatCurrency(totalCost)}
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <FormControl fullWidth>
                <InputLabel>Ficha técnica de</InputLabel>
                <Select
                  label="Ficha técnica de"
                  value={selectedTarget?.key || 'product'}
                  onChange={(event) => setTargetKey(event.target.value)}
                >
                  {technicalTargets.map((target) => (
                    <MenuItem key={target.key} value={target.key}>
                      {target.label}{target.subtitle ? ` • ${target.subtitle}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="outlined" startIcon={<HistoryOutlinedIcon />} onClick={openAuditDialog}>
                Auditoria
              </Button>
            </Stack>

            {error ? <Alert severity="error">{error}</Alert> : null}
            {!canManage ? <Alert severity="info">Seu perfil pode consultar a ficha técnica, mas não editar insumos ou quantidades.</Alert> : null}
            {selectedTarget?.type === 'product' && !productId ? <Alert severity="warning">Produto ainda sem identificador interno.</Alert> : null}
            {!loading && (selectedTarget?.type !== 'product' || productId) && items.length === 0 ? (
              <Alert severity="warning">Este item ainda não possui ficha técnica cadastrada.</Alert>
            ) : null}

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Buscar insumo"
                    value={inputSearch}
                    onChange={(event) => setInputSearch(event.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth>
                    <InputLabel>Insumo</InputLabel>
                    <Select
                      label="Insumo"
                      value={selectedInputId}
                      onChange={(event) => setSelectedInputId(event.target.value)}
                    >
                      {filteredInputs.map((input) => (
                        <MenuItem key={input.id} value={input.id}>
                          {input.name} ({input.baseUnit})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <TextField
                    fullWidth
                    label="Quantidade"
                    type="number"
                    value={quantityBase}
                    onChange={(event) => setQuantityBase(event.target.value)}
                    InputProps={{ endAdornment: selectedInput ? <Chip size="small" label={selectedInput.baseUnit} /> : null }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                    onClick={saveSheetItem}
                    disabled={saving || !canManage || !selectedInputId || !quantityBase || (selectedTarget?.type === 'product' && !productId)}
                    sx={{ minHeight: 54 }}
                  >
                    Adicionar
                  </Button>
                </Grid>
              </Grid>
              {canManage ? (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mt: 2 }}>
                  <Button
                    startIcon={<Inventory2OutlinedIcon />}
                    variant="outlined"
                    onClick={() => {
                      setInputForm(emptyInputForm);
                      setQuickCategoryName('');
                      setInputDialogError('');
                      setInputDialogOpen(true);
                    }}
                  >
                    Novo insumo
                  </Button>
                  <Button startIcon={<CategoryOutlinedIcon />} variant="outlined" onClick={() => { setCategoryDialogError(''); setCategoryDialogOpen(true); }}>
                    Nova categoria
                  </Button>
                </Stack>
              ) : null}
            </Paper>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {items.map((item) => (
                  <Paper key={item.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid size={{ xs: 12, md: 5 }}>
                        <Typography sx={{ fontWeight: 800 }}>{item.inputName}</Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 0.8, flexWrap: 'wrap' }}>
                          <Chip size="small" label={item.categoryName || 'Sem categoria'} />
                          <Chip size="small" color={item.active ? 'success' : 'default'} label={item.active ? 'Ativo' : 'Inativo'} />
                        </Stack>
                      </Grid>
                      <Grid size={{ xs: 7, md: 3 }}>
                        <TextField
                          fullWidth
                          label="Quantidade base"
                          type="number"
                          value={quantityDrafts[item.id] ?? item.quantityBase}
                          onChange={(event) => setQuantityDrafts((prev) => ({ ...prev, [item.id]: event.target.value }))}
                          InputProps={{ endAdornment: <Chip size="small" label={item.baseUnit} /> }}
                        />
                      </Grid>
                      <Grid size={{ xs: 5, md: 2 }}>
                        <Typography variant="body2" color="text.secondary">Custo</Typography>
                        <Typography sx={{ fontWeight: 800 }}>{formatCurrency(item.lineCost)}</Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 2 }}>
                        <Stack direction="row" spacing={0.8} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                          <Tooltip title="Atualizar quantidade">
                            <IconButton color="primary" onClick={() => updateSheetItem(item.id)} disabled={saving || !canManage}>
                              <SaveOutlinedIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remover da ficha">
                            <IconButton color="error" onClick={() => removeSheetItem(item.id)} disabled={saving || !canManage}>
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Nova categoria</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {categoryDialogError ? <Alert severity="error">{categoryDialogError}</Alert> : null}
            <TextField
              autoFocus
              fullWidth
              label="Nome da categoria"
              value={categoryName}
              onChange={(event) => setCategoryName(event.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={createCategory} disabled={saving || !canManage || !categoryName.trim()}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={inputDialogOpen} onClose={() => setInputDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Novo insumo</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {inputDialogError ? <Alert severity="error">{inputDialogError}</Alert> : null}
            <TextField
              fullWidth
              label="Nome"
              value={inputForm.name}
              onChange={(event) => setInputForm((prev) => ({ ...prev, name: event.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>Categoria</InputLabel>
              <Select
                label="Categoria"
                value={inputForm.categoryId}
                onChange={(event) => setInputForm((prev) => ({ ...prev, categoryId: event.target.value }))}
              >
                <MenuItem value="">Sem categoria</MenuItem>
                {categories.filter((category) => category.active !== false).map((category) => (
                  <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nova categoria"
                  value={quickCategoryName}
                  onChange={(event) => setQuickCategoryName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      createQuickCategory();
                    }
                  }}
                />
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={createQuickCategory}
                  disabled={saving || !quickCategoryName.trim()}
                  sx={{ minWidth: { xs: '100%', sm: 210 }, whiteSpace: 'nowrap' }}
                >
                  Criar e selecionar
                </Button>
              </Stack>
            </Paper>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Unidade de compra</InputLabel>
                  <Select
                    label="Unidade de compra"
                    value={inputForm.purchaseUnit}
                    onChange={(event) => setInputForm((prev) => ({ ...prev, purchaseUnit: event.target.value, quantityPerPackage: '' }))}
                  >
                    <MenuItem value="Kg">Kg</MenuItem>
                    <MenuItem value="L">L</MenuItem>
                    <MenuItem value="Cx">Cx</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth label="Unidade base" value={conversion.baseUnit} disabled />
              </Grid>
            </Grid>
            {inputForm.purchaseUnit === 'Cx' ? (
              <TextField
                fullWidth
                required
                label="Unidades por caixa"
                type="number"
                value={inputForm.quantityPerPackage}
                onChange={(event) => setInputForm((prev) => ({ ...prev, quantityPerPackage: event.target.value }))}
              />
            ) : null}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Estoque inicial"
                  type="number"
                  value={inputForm.currentStockBase}
                  onChange={(event) => setInputForm((prev) => ({ ...prev, currentStockBase: event.target.value }))}
                  InputProps={{ endAdornment: <Chip size="small" label={conversion.baseUnit} /> }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Estoque mínimo"
                  type="number"
                  value={inputForm.minimumStockBase}
                  onChange={(event) => setInputForm((prev) => ({ ...prev, minimumStockBase: event.target.value }))}
                  InputProps={{ endAdornment: <Chip size="small" label={conversion.baseUnit} /> }}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Custo da compra"
                  type="number"
                  value={inputForm.purchaseCost}
                  onChange={(event) => setInputForm((prev) => ({ ...prev, purchaseCost: event.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth label="Custo por unidade base" value={formatCurrency(baseUnitCost)} disabled />
              </Grid>
            </Grid>
            <Divider />
            <Typography variant="body2" color="text.secondary">
              Conversão: 1 {inputForm.purchaseUnit} = {conversion.conversionFactor || 0} {conversion.baseUnit}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInputDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={createInput} disabled={saving || !canManage || !inputForm.name.trim()}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={auditDialogOpen} onClose={() => setAuditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Auditoria da ficha técnica</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {loadingAudit ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : audit.length === 0 ? (
              <Typography color="text.secondary">Nenhuma alteração registrada.</Typography>
            ) : audit.map((entry) => (
              <Paper key={entry.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography sx={{ fontWeight: 800 }}>{entry.action} • {entry.inputName || 'Insumo'}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {entry.previousQuantityBase ?? '-'} → {entry.newQuantityBase ?? '-'} {entry.baseUnit} • {new Date(entry.createdAt).toLocaleString('pt-BR')}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditDialogOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProductTechnicalSheetDialog;
